import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { CardHead, ErrorBanner } from '../../components/ui/Primitives.js';

/**
 * The company caller ID (0142): the number calls show when an extension has none of its own
 * (owner, 27 September 2026). Saved as soon as it is chosen; the switch has it at once.
 */
export function CompanyCallerId({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const key = ['tenants', tenantId, 'company-caller-id'] as const;
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ default_caller_id: string | null; in_use: string | null; numbers: string[] }>(
      `/v1/tenants/${tenantId}/company-caller-id`),
  });
  const save = useMutation({
    mutationFn: (v: string | null) => api.put(`/v1/tenants/${tenantId}/company-caller-id`, { default_caller_id: v }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const d = q.data;
  const first = d?.numbers[0] ?? null;

  return (
    <div className="arc-card" style={{ marginBottom: 16 }}>
      <CardHead title="Company caller ID" sub="Shown on calls from extensions that have no caller ID of their own" />
      <div className="arc-card-body">
        {q.error && <ErrorBanner error={q.error} />}
        {save.error && <ErrorBanner error={save.error} />}
        {d && d.numbers.length === 0 && (
          <p className="arc-muted" style={{ margin: 0 }}>This account has no numbers yet.</p>
        )}
        {d && d.numbers.length > 0 && (
          <div className="arc-field" style={{ margin: 0, maxWidth: 360 }}>
            <label className="arc-label" htmlFor="company-cli">Number</label>
            <select id="company-cli" className="arc-select" value={d.default_caller_id ?? ''}
              disabled={!can('outboundroute:write') || save.isPending}
              onChange={(e) => save.mutate(e.target.value || null)}>
              <option value="">Not chosen: the account's first number{first ? ` (${first})` : ''}</option>
              {d.numbers.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <div className="arc-hint">An extension's own caller ID, set on the extension, always comes first.</div>
          </div>
        )}
      </div>
    </div>
  );
}
